import { useTranslation } from 'react-i18next';
import type { TrialCustomerIdentity } from '../types/trialCustomerIdentity';
import { hasActionableCustomerIdentity } from '../types/trialCustomerIdentity';
import { safeDisplay } from '../utils/trialHelpers';

interface TrialCustomerIdentitySectionProps {
  identity: TrialCustomerIdentity;
}

function IdentityRow({
  label,
  value,
  unavailableLabel,
  emphasize,
}: {
  label: string;
  value: string | null;
  unavailableLabel: string;
  emphasize?: boolean;
}) {
  const display = safeDisplay(value);
  return (
    <div className="grid grid-cols-1 gap-0.5 sm:grid-cols-3 sm:gap-3 min-w-0">
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd
        className={
          emphasize
            ? 'sm:col-span-2 text-sm font-semibold text-foreground break-words min-w-0'
            : 'sm:col-span-2 text-sm text-foreground break-words min-w-0'
        }
      >
        {display ? (
          <span className="break-words">{display}</span>
        ) : (
          <span className="text-muted-foreground italic">{unavailableLabel}</span>
        )}
      </dd>
    </div>
  );
}

export function TrialCustomerIdentitySection({ identity }: TrialCustomerIdentitySectionProps) {
  const { t } = useTranslation();
  const unavailable = t('trials.identity.unavailable');
  const confirmed = hasActionableCustomerIdentity(identity);

  return (
    <section
      className="rounded-xl border border-border bg-muted/20 p-4 space-y-3 min-w-0"
      aria-labelledby="trial-customer-identity-heading"
    >
      <div className="flex flex-wrap items-center gap-2">
        <h3 id="trial-customer-identity-heading" className="text-sm font-semibold text-foreground">
          {t('trials.identity.sectionTitle')}
        </h3>
        <span
          className={
            confirmed
              ? 'inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
              : 'inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
          }
        >
          {confirmed ? t('trials.identity.statusConfirmed') : t('trials.identity.statusIncomplete')}
        </span>
      </div>

      {!confirmed ? (
        <p className="text-xs text-muted-foreground">{t('trials.identity.incompleteHint')}</p>
      ) : null}

      <dl className="space-y-3">
        <IdentityRow
          label={t('trials.identity.customerName')}
          value={identity.customerName}
          unavailableLabel={unavailable}
          emphasize
        />
        <IdentityRow
          label={t('trials.identity.customerEmail')}
          value={identity.customerEmail}
          unavailableLabel={unavailable}
          emphasize
        />
        <IdentityRow
          label={t('trials.identity.customerPhone')}
          value={identity.customerPhone}
          unavailableLabel={unavailable}
        />
        <IdentityRow
          label={t('trials.identity.company')}
          value={identity.tenantName}
          unavailableLabel={unavailable}
          emphasize
        />
        <IdentityRow
          label={t('trials.identity.country')}
          value={identity.country}
          unavailableLabel={unavailable}
        />
      </dl>
    </section>
  );
}
